import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Card,
  Skeleton,
  Breadcrumbs,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  EmojiEvents as TrophyIcon,
  NavigateNext as NavigateNextIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

// Services & Web3
import cinstance from "../../Service/randomcinstance";
import web3 from "../../Service/web3";
import NavBar from "../../Components/NavBar";
import { colors, borderRadiusPx, customShadows } from "../../../theme";
import {
  LOTTERY_TYPES,
  STATUS_CONFIG,
  lotteryTypeMapper,
  resultStatusTypeMapper,
  Path,
} from "../../../static/Constants";
import { CommonButton, CommonChip, CommonTable } from "../../Components/ui";

const NoWinner = "0x0000000000000000000000000000000000000000";

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const formatRolloverTime = (seconds) => {
  if (!seconds || Number(seconds) <= 0) return "N/A";
  const numSec = Number(seconds);
  const days = Math.floor(numSec / (60 * 60 * 24));
  const hours = Math.floor((numSec % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((numSec % (60 * 60)) / 60);

  let formattedTime = "";
  if (days > 0) formattedTime += `${days}d `;
  if (hours > 0) formattedTime += `${hours}h `;
  if (minutes > 0) formattedTime += `${minutes}m`;
  return formattedTime.trim() || "< 1m";
};

const UserLotteryDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [lottery, setLottery] = useState(null);
  const [datelist, setDateList] = useState(null);
  const [ticketLedger, setTicketLedger] = useState([]);
  const [winnersEvent, setWinnersEvent] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);

  /**
   * Clipboard Helper
   */
  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
    toast.success("Copied to clipboard!");
  };

  /**
   * Fetch On-Chain Data
   */
  const fetchLotteryData = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    try {
      if (!window.ethereum && !web3) {
        setLoading(false);
        return;
      }

      window.web3 = web3;

      // 1. Fetch Main Lottery Parameters
      const lotteryRes = await cinstance.methods.lottery(id).call();
      if (!lotteryRes || !lotteryRes.lotteryId || lotteryRes.lotteryId === "0") {
        setLottery(null);
        setLoading(false);
        return;
      }
      setLottery(lotteryRes);

      // 2. Fetch Lottery Schedule / Dates
      try {
        const datesRes = await cinstance.methods.lotteryDates(id).call();
        setDateList(datesRes);
      } catch (dateErr) {
        console.warn("Could not fetch lottery dates:", dateErr);
      }

      // 3. Fetch Past Winner Events
      try {
        cinstance.getPastEvents(
          "LotteryResult",
          {
            filter: { lotteryId: `${id}` },
            fromBlock: 0,
            toBlock: "latest",
          },
          (errors, events) => {
            if (!errors && events) {
              setWinnersEvent(events);
            }
          }
        );
      } catch (evtErr) {
        console.warn("Could not fetch LotteryResult events:", evtErr);
      }

      // 4. Fetch Ticket Ledger
      try {
        const numbersRes = await cinstance.methods.getLotteryNumbers(id).call();
        const parsedTickets = [];

        if (numbersRes && numbersRes.useraddress) {
          const userAddresses = numbersRes.useraddress || [];
          const rawTickets = numbersRes.tickets || [];
          const pickSize = Number(lotteryRes.pickNumbers || 1);

          userAddresses.forEach((userAddr, index) => {
            let userPickedNumbers = [];
            if (lotteryRes.lotteryType !== "3") {
              userPickedNumbers = rawTickets.slice(
                index * pickSize,
                (index + 1) * pickSize
              );
            } else {
              const ticketVal = rawTickets[index];
              userPickedNumbers = ticketVal
                ? ticketVal.toString().split("").map(Number)
                : [];
            }

            parsedTickets.push({
              id: `${userAddr}-${index}`,
              useraddress: userAddr,
              tickets: userPickedNumbers,
            });
          });
        }
        setTicketLedger(parsedTickets);
      } catch (numErr) {
        console.warn("Could not fetch lottery numbers:", numErr);
      }
    } catch (err) {
      console.error("Error fetching lottery details:", err);
      toast.error("Failed to load lottery data from blockchain.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLotteryData();
  }, [fetchLotteryData]);

  // Derived Game Type and Status
  const typeId = lottery ? Number(lottery.lotteryType) : 0;
  const statusId = lottery ? Number(lottery.status) : 0;

  const gameTypeConfig = LOTTERY_TYPES[typeId] || {
    name: lotteryTypeMapper[typeId] || `Lottery Type ${typeId}`,
    shortName: "Lottery",
    color: colors.primary.main,
    gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
  };

  const statusConfig = STATUS_CONFIG[statusId] || {
    label: resultStatusTypeMapper[statusId] || "Unknown",
    variant: "neutral",
  };

  const isOpen = statusId === 0 || statusId === 5;
  const hasWinner =
    lottery &&
    lottery.lotteryWinner &&
    lottery.lotteryWinner !== NoWinner;

  const winningNumber = useMemo(() => {
    if (!winnersEvent || winnersEvent.length === 0) return null;
    const evt = winnersEvent[0];
    if (evt?.returnValues?.n) return evt.returnValues.n;
    if (evt?.returnValues?.number) {
      return evt.returnValues.number
        .split("-")
        .slice(0, -1)
        .reverse()
        .join("-");
    }
    return null;
  }, [winnersEvent]);

  // Determine Game Play Link
  const playLink = useMemo(() => {
    if (typeId === 0) return Path.getRevolverLottery(id);
    if (typeId === 1) return Path.getMineLottery(id);
    if (typeId === 3) return Path.getMissileLottery(id);
    return Path.allLotteries;
  }, [typeId, id]);

  // Ticket Table Columns
  const ticketColumns = useMemo(
    () => [
      {
        id: "useraddress",
        label: "Bought By",
        minWidth: 260,
        render: (_, row) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: colors.text.primary,
              }}
            >
              {row?.useraddress}
            </Typography>
            <Tooltip
              title={
                copiedKey === `buyer-${row.id}` ? "Copied!" : "Copy Address"
              }
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(row?.useraddress, `buyer-${row.id}`);
                }}
                sx={{ p: 0.5, color: colors.slate[400] }}
              >
                {copiedKey === `buyer-${row.id}` ? (
                  <CheckIcon
                    sx={{ fontSize: 15, color: colors.success.main }}
                  />
                ) : (
                  <CopyIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      {
        id: "tickets",
        label: "Numbers Picked",
        minWidth: 300,
        render: (_, row) => (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.75 }}>
            {row?.tickets && row.tickets.length > 0 ? (
              row.tickets.map((num, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    bgcolor: "rgba(59, 130, 246, 0.15)",
                    color: colors.primary.light || "#60a5fa",
                    border: `1px solid rgba(59, 130, 246, 0.35)`,
                  }}
                >
                  {num}
                </Box>
              ))
            ) : (
              <Typography variant="caption" sx={{ color: colors.slate[400] }}>
                No numbers recorded
              </Typography>
            )}
          </Stack>
        ),
      },
    ],
    [copiedKey]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#070b14",
        color: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      <NavBar />

      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: colors.slate[500] }} />}
          sx={{ mb: 3 }}
        >
          <Link
            to={Path.root}
            style={{
              color: colors.slate[400],
              textDecoration: "none",
              fontSize: "0.875rem",
            }}
          >
            Home
          </Link>
          <Link
            to={Path.allLotteries}
            style={{
              color: colors.slate[400],
              textDecoration: "none",
              fontSize: "0.875rem",
            }}
          >
            All Lotteries
          </Link>
          <Typography
            sx={{
              color: colors.primary.main,
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            Lottery #{id}
          </Typography>
        </Breadcrumbs>

        {/* Back Link & Header Bar */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <CommonButton
            variant="outline"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push(Path.allLotteries)}
            sx={{
              color: colors.slate[300],
              borderColor: "rgba(255, 255, 255, 0.15)",
              "&:hover": {
                borderColor: colors.primary.main,
                bgcolor: "rgba(59, 130, 246, 0.1)",
              },
            }}
          >
            Back to Lotteries
          </CommonButton>

          {isOpen && (
            <CommonButton
              variant="primary"
              startIcon={<PlayIcon />}
              onClick={() => history.push(playLink)}
              sx={{
                px: 3,
                py: 1,
                fontSize: "0.95rem",
                fontWeight: 700,
                background: gameTypeConfig.gradient,
                boxShadow: "0 4px 14px 0 rgba(59, 130, 246, 0.4)",
              }}
            >
              Play Now
            </CommonButton>
          )}
        </Stack>

        {/* Loading State */}
        {loading && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton
                  variant="rounded"
                  height={160}
                  animation="wave"
                  sx={{ bgcolor: "rgba(255, 255, 255, 0.05)", borderRadius: borderRadiusPx.xl }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton
                  variant="rounded"
                  height={220}
                  animation="wave"
                  sx={{ bgcolor: "rgba(255, 255, 255, 0.05)", borderRadius: borderRadiusPx.xl }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton
                  variant="rounded"
                  height={220}
                  animation="wave"
                  sx={{ bgcolor: "rgba(255, 255, 255, 0.05)", borderRadius: borderRadiusPx.xl }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Not Found State */}
        {!loading && !lottery && (
          <Card
            sx={{
              p: 6,
              textAlign: "center",
              bgcolor: "rgba(15, 23, 42, 0.75)",
              backdropFilter: "blur(12px)",
              borderRadius: borderRadiusPx.xl,
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#ffffff", mb: 1.5 }}>
              Lottery Not Found
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[400], mb: 3 }}>
              Could not locate lottery #{id} on the blockchain.
            </Typography>
            <CommonButton
              variant="primary"
              onClick={() => history.push(Path.allLotteries)}
            >
              Explore All Lotteries
            </CommonButton>
          </Card>
        )}

        {/* Loaded Lottery Details */}
        {!loading && lottery && (
          <Stack spacing={3.5}>
            {/* 1. Hero / Title Banner */}
            <Card
              sx={{
                p: { xs: 2.5, sm: 3.5 },
                bgcolor: "rgba(15, 23, 42, 0.8)",
                backdropFilter: "blur(16px)",
                borderRadius: borderRadiusPx.xl,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: customShadows.card,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: gameTypeConfig.gradient,
                }}
              />
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "#ffffff",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      Lottery #{id}
                    </Typography>
                    <CommonChip
                      label={gameTypeConfig.name}
                      variant="primary"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    <CommonChip
                      label={statusConfig.label}
                      variant={statusConfig.variant}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: colors.slate[400], fontWeight: 600 }}>
                      Created By:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        color: colors.slate[200],
                        fontWeight: 600,
                      }}
                    >
                      {lottery.ownerAddress}
                    </Typography>
                    <Tooltip
                      title={
                        copiedKey === "creator-addr" ? "Copied!" : "Copy Address"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(lottery.ownerAddress, "creator-addr")}
                        sx={{ p: 0.5, color: colors.slate[400] }}
                      >
                        {copiedKey === "creator-addr" ? (
                          <CheckIcon
                            sx={{ fontSize: 15, color: colors.success.main }}
                          />
                        ) : (
                          <CopyIcon sx={{ fontSize: 15 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>

                {/* Prize Pool Highlight */}
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                  <Typography variant="caption" sx={{ color: colors.slate[400], fontWeight: 700, textTransform: "uppercase" }}>
                    Total Prize Pool
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      background: "linear-gradient(135deg, #60a5fa 0%, #38bdf8 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mt: 0.5,
                    }}
                  >
                    {safeFromWei(lottery.totalPrize)} POL
                  </Typography>
                </Grid>
              </Grid>
            </Card>

            {/* 2. Winner Banner (if resolved) */}
            {hasWinner && (
              <Card
                sx={{
                  p: 3,
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  borderRadius: borderRadiusPx.xl,
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    bgcolor: "rgba(16, 185, 129, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.emerald[400],
                  }}
                >
                  <TrophyIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: colors.emerald[400], fontWeight: 700 }}>
                    Winner Declared!
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        color: "#ffffff",
                        fontWeight: 700,
                      }}
                    >
                      {lottery.lotteryWinner}
                    </Typography>
                    <Tooltip
                      title={
                        copiedKey === "winner-addr" ? "Copied!" : "Copy Address"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(lottery.lotteryWinner, "winner-addr")}
                        sx={{ p: 0.5, color: colors.slate[400] }}
                      >
                        {copiedKey === "winner-addr" ? (
                          <CheckIcon
                            sx={{ fontSize: 15, color: colors.success.main }}
                          />
                        ) : (
                          <CopyIcon sx={{ fontSize: 15 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {winningNumber && (
                    <Typography variant="caption" sx={{ color: colors.slate[300], mt: 0.5, display: "block" }}>
                      Winning Number: <strong>{winningNumber}</strong>
                    </Typography>
                  )}
                </Box>
              </Card>
            )}

            {/* 3. Specs & Schedule Grid */}
            <Grid container spacing={3}>
              {/* Left Column: Lottery Specifications */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 3,
                    bgcolor: "rgba(15, 23, 42, 0.8)",
                    backdropFilter: "blur(16px)",
                    borderRadius: borderRadiusPx.xl,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#ffffff", mb: 2.5 }}
                  >
                    Lottery Specifications
                  </Typography>

                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                        Entry Fee
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: colors.amber[400] }}>
                        {safeFromWei(lottery.entryFee)} POL
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                        Total Capacity / Numbers
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#ffffff" }}>
                        {lottery.capacity || "N/A"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                        Pick Numbers
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#ffffff" }}>
                        {lottery.pickNumbers || "1"}
                      </Typography>
                    </Stack>

                    {lottery.lotteryType === "2" && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                          Minimum Players
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#ffffff" }}>
                          {lottery.minPlayers}
                        </Typography>
                      </Stack>
                    )}

                    {lottery.lotteryType !== "1" && lottery.rolloverperct && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                          Rollover Percentage
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.cyan[400] }}>
                          {lottery.rolloverperct}%
                        </Typography>
                      </Stack>
                    )}

                    {lottery.partnershare && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                          Partner's Share
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.purple[400] }}>
                          {lottery.partnershare}%
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Card>
              </Grid>

              {/* Right Column: Timeline & Dates */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 3,
                    bgcolor: "rgba(15, 23, 42, 0.8)",
                    backdropFilter: "blur(16px)",
                    borderRadius: borderRadiusPx.xl,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#ffffff", mb: 2.5 }}
                  >
                    Timeline & Schedule
                  </Typography>

                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                        Start Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#ffffff" }}>
                        {datelist?.startTime
                          ? moment.unix(datelist.startTime).format("DD/MM/YYYY hh:mma")
                          : "N/A"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                        Draw / Rollover Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#ffffff" }}>
                        {datelist?.drawTime
                          ? moment.unix(datelist.drawTime).format("DD/MM/YYYY hh:mma")
                          : "N/A"}
                      </Typography>
                    </Stack>

                    {(lottery.lotteryType === "2" || lottery.lotteryType === "3") && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                          Rollover Duration
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary.light }}>
                          {formatRolloverTime(datelist?.rolloverdays)}
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                        Deployed On
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#ffffff" }}>
                        {lottery.deployedOn
                          ? moment.unix(lottery.deployedOn).format("DD/MM/YYYY")
                          : "N/A"}
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            </Grid>

            {/* 4. Ticket Buyers Ledger */}
            <Card
              sx={{
                bgcolor: "rgba(15, 23, 42, 0.8)",
                backdropFilter: "blur(16px)",
                borderRadius: borderRadiusPx.xl,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 3, borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#ffffff" }}>
                  Tickets Purchased ({ticketLedger.length})
                </Typography>
                <Typography variant="caption" sx={{ color: colors.slate[400] }}>
                  Decentralized transparent on-chain participant ledger for this lottery.
                </Typography>
              </Box>

              <CommonTable
                columns={ticketColumns}
                rows={ticketLedger}
                rowKey="id"
                emptyMessage="No tickets purchased yet for this lottery."
              />
            </Card>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default UserLotteryDetails;
